/**
 * @see docs/worklog/TW-898-quote-email-status/rfc.md
 */
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useColumnSortFunc } from "../blocks/ColumnSort/useColumnSortFunc";
import { useOrdersCriteriaPredicate } from "../blocks/MyQuotes/filters/Criteria/useOrdersCriteriaPredicate";
import { useOrdersDatePredicate } from "../blocks/MyQuotes/filters/Date/useOrdersDatePredicate";
import { useOrdersSizeLimit } from "../blocks/MyQuotes/filters/Size/useOrdersSizeLimit";
import { useLanguage } from "../i18n/useLanguage";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { useUrl } from "../stores/hooks/useUrl";
import { EmailTrackingSummary, OrderDetails } from "../stores/slices/cartSlice";

const dateFields = new Set<string>(["dateCreated"]);
const numericFields = new Set(["quoteNumber", "quoteTotal"]);

const knownEmailStatusKey: Record<string, string> = {
  bounced: "quoteReport.emailBounced",
  clicked: "quoteReport.emailClicked",
  delivered: "quoteReport.emailDelivered",
  failed: "quoteReport.emailFailed",
  opened: "quoteReport.emailOpened",
  sent: "quoteReport.emailSent",
};

const formatEmailStatus = (
  summary: EmailTrackingSummary | undefined,
  t: TFunction,
): string => {
  if (!summary) return t("quoteReport.notAvailable");
  if (summary.is_failed) return t("quoteReport.emailFailed");
  if (summary.status === "not_sent") return t("quoteReport.emailNotSent");
  if (Number(summary.opened_count) > 0) {
    return t("quoteReport.emailOpenedCount", {
      count: Number(summary.opened_count),
    });
  }
  const key = knownEmailStatusKey[summary.status];
  return key ? t(key) : summary.status;
};

export const useGetOrderHistory = (): ModifiedSWRResponse<
  FetcherResult<Payload>,
  MappedQuoteItem[]
> => {
  const { t } = useTranslation();
  const { dealerId } = useDealerAccountInfo();
  const { search } = useUrl();
  const [language] = useLanguage();
  const size = useOrdersSizeLimit();
  const datePredicate = useOrdersDatePredicate();
  const criteriaPredicate = useOrdersCriteriaPredicate();
  const sortFunc = useColumnSortFunc("quoteReport", {
    dateFields,
    numericFields,
  });
  const { data, ...rest } = useSwrGet<OrderDetails[]>(
    "/retailSiteWebService/shopping/history/getOrderHistory.php",
    {
      dealerId,
    },
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
    },
  );
  const result = useMemo(
    () =>
      data
        ?.filter(datePredicate)
        .filter(criteriaPredicate)
        .map(
          ({
            customerEmail,
            customerName,
            customerNotes,
            customerPhone,
            emailTrackingSummary,
            isNational,
            orderAbsoluteUrl,
            orderCreatedTimestamp,
            orderCreatedUserName,
            orderFulfillmentList,
            orderGrandTotal,
            orderId,
            orderStatusDescription,
          }) => {
            return {
              createdBy: orderCreatedUserName,
              customerEmail,
              customerName,
              customerNotes: t(
                customerNotes ? "quoteReport.yes" : "quoteReport.notAvailable",
              ),
              customerPhone,
              dateCreated: orderCreatedTimestamp,
              emailStatus: formatEmailStatus(emailTrackingSummary, t),
              orderPrinted:
                orderFulfillmentList
                  .map(
                    ({ fulfillment_value }) =>
                      fulfillment_value?.customerPoNumber,
                  )
                  .filter(Boolean)
                  .join(", ") || t("quoteReport.notAvailable"),
              priceMatch:
                orderFulfillmentList
                  .map(
                    ({ fulfillment_value }) => fulfillment_value?.orderNumber,
                  )
                  .filter(Boolean)
                  .join(", ") || t("quoteReport.notAvailable"),
              quoteNumber: orderId,
              quoteTotal: orderGrandTotal,
              status: orderStatusDescription[language],
              type: t(
                Number(isNational)
                  ? "quoteReport.isNational"
                  : "quoteReport.isDealer",
              ),
              viewQuotes: orderAbsoluteUrl[language],
            };
          },
        )
        .sort(sortFunc)
        .slice(0, size),
    [data, search, language, sortFunc, datePredicate, criteriaPredicate],
  );
  return { data: result, ...rest };
};

type Payload = OrderDetails[];

export type MappedQuoteItem = {
  createdBy: string;
  customerEmail: string;
  customerName: string;
  customerNotes: string;
  customerPhone: string;
  dateCreated: string;
  emailStatus: string;
  orderPrinted: string;
  priceMatch: string;
  quoteNumber: string;
  quoteTotal: string;
  status: string;
  type: string;
  viewQuotes?: string;
};
